/**
 * DTI Worksheet — Section 5.7 of the architecture blueprint (wired in 5.6).
 *
 * Server snapshots are authoritative: the engine's income ladder is doc-only
 * (validated dossier slots) and debts come from tradelines + manual debts, so
 * income/debt are read-only here. The agent drives the loan-side knobs:
 *
 *   - live local preview of P&I + front/back DTI as you type (excludes
 *     program MI — the engine adds PMI/MIP/funding fees on recalc)
 *   - Recalculate → POST .../dti/calculate with the loan inputs
 *   - 4-program qualification cards from the snapshot's matrix
 *   - overrides with mandatory reason (line-level + loan-level), audited
 *   - named what-if scenarios with side-by-side compare
 *
 * Auto-recalc snapshots (dossier validation, 1003 edits) land via polling —
 * the EventBridge→AppSync `dti.recalculated` push is deferred to the deploy
 * checkpoint. Styling lives in DTIWorksheet.css (`.dti-`-prefixed).
 */

import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  DtiCalculation,
  DtiLineItem,
  LoanInputs,
  LoanProgram,
  ProgramKey,
  Verdict,
} from "@re-platform/types";
import { ApiClientError, mortgageService } from "@re-platform/api-client";
import {
  Button,
  FormField,
  Skeleton,
  StatusPill,
  Toast,
} from "@re-platform/ui";

import { api } from "../../lib/api";

import "./DTIWorksheet.css";

const VERDICT_ACCENT: Record<Verdict, string> = {
  PASS: "var(--rp-color-success)",
  BORDERLINE: "var(--rp-color-warning)",
  FAIL: "var(--rp-color-danger)",
};

const VERDICT_TONE: Record<Verdict, "success" | "warning" | "danger"> = {
  PASS: "success",
  BORDERLINE: "warning",
  FAIL: "danger",
};

const PROGRAM_KEYS: readonly ProgramKey[] = ["conv", "fha", "va", "usda"];

const PROGRAM_LABEL: Record<ProgramKey, string> = {
  conv: "Conventional",
  fha: "FHA",
  va: "VA",
  usda: "USDA",
};

/** Loan-level override targets (engine LOAN_OVERRIDE_FIELDS). */
const LOAN_FIELDS = [
  "property_value",
  "down_payment",
  "interest_rate_bps",
  "loan_term_months",
  "loan_program",
  "annual_property_tax",
  "annual_insurance",
  "monthly_hoa",
] as const;

/** Bars are scaled against a 0–60% DTI domain. */
const DTI_DOMAIN = 0.6;

interface LoanFormValues {
  propertyValue: number;
  downPayment: number;
  /** Annual rate in percent (7.0 = 700 bps). */
  interestRatePct: number;
  loanTermMonths: number;
  loanProgram: LoanProgram;
  annualPropertyTax: number;
  annualInsurance: number;
  monthlyHoa: number;
}

function num(value: string | null): number {
  return value === null ? 0 : Number(value);
}

function bpsPct(bps: number | null | undefined): string {
  return bps == null ? "—" : `${(bps / 100).toFixed(1)}%`;
}

function money(value: string | number | null | undefined): string {
  if (value == null) return "—";
  const parsed = typeof value === "number" ? value : Number(value);
  return parsed.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

/** Monthly P&I on a fully-amortizing loan. */
function monthlyPI(
  principal: number,
  ratePct: number,
  termMonths: number,
): number {
  if (principal <= 0 || termMonths <= 0) return 0;
  const r = ratePct / 100 / 12;
  if (r === 0) return principal / termMonths;
  return (principal * r) / (1 - Math.pow(1 + r, -termMonths));
}

export function DTIWorksheet() {
  const { applicationId = "" } = useParams<{ applicationId: string }>();
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const dtiQuery = useQuery({
    queryKey: ["dti", applicationId],
    queryFn: () => mortgageService.getDti(api, applicationId),
    retry: (failureCount, error) =>
      !(error instanceof ApiClientError && error.status === 404) &&
      failureCount < 2,
    refetchInterval: 15_000,
  });
  const calc = dtiQuery.data;
  const noCalcYet =
    dtiQuery.isError &&
    dtiQuery.error instanceof ApiClientError &&
    dtiQuery.error.status === 404;

  const scenariosQuery = useQuery({
    queryKey: ["dti-scenarios", applicationId],
    queryFn: () => mortgageService.listScenarios(api, applicationId),
    enabled: calc !== undefined,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["dti", applicationId] });
    void queryClient.invalidateQueries({
      queryKey: ["dti-scenarios", applicationId],
    });
  };

  const { register, reset, watch } = useForm<LoanFormValues>({
    defaultValues: {
      propertyValue: 0,
      downPayment: 0,
      interestRatePct: 7,
      loanTermMonths: 360,
      loanProgram: "conventional",
      annualPropertyTax: 0,
      annualInsurance: 0,
      monthlyHoa: 0,
    },
  });

  // Prefill the knobs from the latest snapshot whenever one lands.
  useEffect(() => {
    if (!calc) return;
    reset({
      propertyValue: num(calc.property_value),
      downPayment: num(calc.down_payment),
      interestRatePct:
        calc.interest_rate_bps != null ? calc.interest_rate_bps / 100 : 7,
      loanTermMonths: calc.loan_term_months ?? 360,
      loanProgram: calc.loan_program ?? "conventional",
      // Tax/insurance aren't on the snapshot — prefill the engine's TX
      // defaults (2% / 0.35% of value) so the preview tracks the server.
      annualPropertyTax: Math.round(num(calc.property_value) * 0.02),
      annualInsurance: Math.round(num(calc.property_value) * 0.0035),
      monthlyHoa: 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calc?.id]);

  const values = watch();

  function loanInputs(): LoanInputs {
    return {
      property_value: values.propertyValue || undefined,
      down_payment: values.downPayment >= 0 ? values.downPayment : undefined,
      interest_rate_bps: Math.round(values.interestRatePct * 100),
      loan_term_months: values.loanTermMonths || undefined,
      loan_program: values.loanProgram,
      annual_property_tax: values.annualPropertyTax || undefined,
      annual_insurance: values.annualInsurance || undefined,
      monthly_hoa: values.monthlyHoa || undefined,
    };
  }

  async function run<T>(work: () => Promise<T>, doneMessage: string) {
    setBusy(true);
    try {
      await work();
      setToast(doneMessage);
      refresh();
    } catch (err) {
      setToast(err instanceof Error ? err.message : "Request failed");
    } finally {
      setBusy(false);
    }
  }

  // -- live local preview (excludes program MI; the engine adds it) ----------
  const income = num(calc?.gross_monthly_income ?? null);
  const debt = num(calc?.total_monthly_debts ?? null);
  const previewPiti =
    monthlyPI(
      values.propertyValue - values.downPayment,
      values.interestRatePct,
      values.loanTermMonths,
    ) +
    values.annualPropertyTax / 12 +
    values.annualInsurance / 12 +
    values.monthlyHoa;
  const previewFront = income > 0 ? previewPiti / income : 0;
  const previewBack = income > 0 ? (previewPiti + debt) / income : 0;

  const incomeLines = useMemo(
    () => (calc?.line_items ?? []).filter((line) => line.kind === "income"),
    [calc],
  );
  const debtLines = useMemo(
    () => (calc?.line_items ?? []).filter((line) => line.kind === "debt"),
    [calc],
  );

  // -- render ------------------------------------------------------------------

  if (dtiQuery.isPending) {
    return (
      <div className="dti-page">
        <Skeleton variant="block" height={120} style={{ marginBottom: 16 }} />
        <Skeleton variant="block" height={420} />
      </div>
    );
  }

  if (noCalcYet) {
    return (
      <div className="dti-page">
        <header className="dti-head">
          <h1>DTI Worksheet</h1>
          <p>No calculation yet for this application.</p>
        </header>
        <Button
          loading={busy}
          onClick={() =>
            void run(
              () => mortgageService.calculateDti(api, applicationId),
              "Calculated.",
            )
          }
        >
          Calculate now
        </Button>
        <Toast message={toast} onDismiss={() => setToast(null)} />
      </div>
    );
  }

  if (dtiQuery.isError || !calc) {
    return (
      <div className="dti-page">
        <header className="dti-head">
          <h1>DTI Worksheet</h1>
          <p>We couldn't load this application's DTI. Please try again.</p>
        </header>
      </div>
    );
  }

  return (
    <div className="dti-page">
      <header className="dti-head">
        <h1>DTI Worksheet</h1>
        <p>
          Income {money(calc.gross_monthly_income)}/mo (doc-verified) · debts{" "}
          {money(calc.total_monthly_debts)}/mo · last computed{" "}
          {new Date(calc.computed_at).toLocaleString()} ({calc.computed_trigger}
          )
        </p>
      </header>

      <div className="dti-grid">
        {/* loan inputs + live preview */}
        <section className="dti-card">
          <div className="dti-card__title">Loan inputs</div>
          <div className="dti-card__sub">
            Live preview as you type — Recalculate runs the full engine (adds
            program MI, persists a snapshot).
          </div>

          <FormField
            label="Property value ($)"
            type="number"
            inputMode="decimal"
            {...register("propertyValue", { valueAsNumber: true, min: 0 })}
          />
          <FormField
            label="Down payment ($)"
            type="number"
            inputMode="decimal"
            {...register("downPayment", { valueAsNumber: true, min: 0 })}
          />
          <FormField
            label="Interest rate (%)"
            type="number"
            step="0.125"
            inputMode="decimal"
            {...register("interestRatePct", { valueAsNumber: true, min: 0 })}
          />
          <FormField
            label="Term (months)"
            type="number"
            {...register("loanTermMonths", { valueAsNumber: true, min: 12 })}
          />
          <label className="dti-select-label">
            Program
            <select className="dti-select" {...register("loanProgram")}>
              <option value="conventional">Conventional</option>
              <option value="fha">FHA</option>
              <option value="va">VA</option>
              <option value="usda">USDA</option>
            </select>
          </label>
          <FormField
            label="Property tax ($/yr)"
            type="number"
            inputMode="decimal"
            {...register("annualPropertyTax", { valueAsNumber: true, min: 0 })}
          />
          <FormField
            label="Insurance ($/yr)"
            type="number"
            inputMode="decimal"
            {...register("annualInsurance", { valueAsNumber: true, min: 0 })}
          />
          <FormField
            label="HOA ($/mo)"
            type="number"
            inputMode="decimal"
            {...register("monthlyHoa", { valueAsNumber: true, min: 0 })}
          />

          <div className="dti-ratios">
            <RatioBar
              label={`Preview PITI ${money(previewPiti)} · front`}
              value={previewFront}
            />
            <RatioBar label="Back-end (preview)" value={previewBack} />
            <div className="dti-preview-note">
              Preview excludes mortgage insurance.
            </div>
          </div>

          <div className="dti-btns">
            <Button
              size="sm"
              loading={busy}
              onClick={() =>
                void run(
                  () =>
                    mortgageService.calculateDti(api, applicationId, {
                      loan_inputs: loanInputs(),
                    }),
                  "Recalculated.",
                )
              }
            >
              Recalculate
            </Button>
            <SaveScenario
              busy={busy}
              onSave={(name) =>
                void run(
                  () =>
                    mortgageService.createScenario(api, {
                      application_id: applicationId,
                      scenario_name: name,
                      loan_inputs: loanInputs(),
                    }),
                  `Scenario "${name}" saved.`,
                )
              }
            />
          </div>
        </section>

        {/* qualification matrix */}
        <div>
          <div className="dti-section">
            Program qualification · snapshot {bpsPct(calc.front_dti_bps)} front
            / {bpsPct(calc.back_dti_bps)} back
          </div>
          <div className="dti-verdicts">
            {PROGRAM_KEYS.map((key) => {
              const entry = calc.qualification[key];
              if (!entry) return null;
              return (
                <VerdictCard
                  key={key}
                  programKey={key}
                  entry={entry}
                  frontBps={calc.front_dti_bps}
                  backBps={calc.back_dti_bps}
                />
              );
            })}
          </div>

          <section className="dti-card dti-gate">
            <div className="dti-card__title">Submission gate</div>
            <p style={{ marginTop: 8 }}>
              Verified-only ratios: <b>{bpsPct(calc.verified_front_dti_bps)}</b>{" "}
              front / <b>{bpsPct(calc.verified_back_dti_bps)}</b> back. Lender
              submission unlocks only when the chosen program passes{" "}
              <b>Verified DTI</b> — the MISMO 3.4 generator refuses{" "}
              <code>bureau_estimate</code> lines. Phase 7 wires submission.
            </p>
          </section>
        </div>
      </div>

      {/* line items + overrides */}
      <LineItemsPanel
        income={incomeLines}
        debts={debtLines}
        calc={calc}
        busy={busy}
        onOverride={(body, label) =>
          void run(
            () =>
              mortgageService.createOverride(api, {
                application_id: applicationId,
                ...body,
              }),
            `Override applied to ${label}.`,
          )
        }
        onRemoveOverride={(overrideId) =>
          void run(
            () => mortgageService.deleteOverride(api, overrideId),
            "Override removed.",
          )
        }
      />

      {/* scenarios */}
      {scenariosQuery.data && scenariosQuery.data.length > 1 && (
        <>
          <div className="dti-section" style={{ marginTop: 24 }}>
            Scenario compare
          </div>
          <div className="dti-table-wrap">
            <table className="dti-table">
              <thead>
                <tr>
                  <th>Scenario</th>
                  <th>Program</th>
                  <th>PITI</th>
                  <th>Front</th>
                  <th>Back</th>
                  <th>Programs</th>
                </tr>
              </thead>
              <tbody>
                {scenariosQuery.data.map((scenario) => (
                  <tr key={scenario.id}>
                    <td>
                      {scenario.scenario_name}
                      {scenario.is_current && (
                        <StatusPill tone="info">live</StatusPill>
                      )}
                    </td>
                    <td>{scenario.loan_program ?? "—"}</td>
                    <td>{money(scenario.piti)}</td>
                    <td>{bpsPct(scenario.front_dti_bps)}</td>
                    <td>{bpsPct(scenario.back_dti_bps)}</td>
                    <td>
                      <span className="dti-chiprow">
                        {PROGRAM_KEYS.map((key) => {
                          const entry = scenario.qualification[key];
                          return entry ? (
                            <StatusPill
                              key={key}
                              tone={VERDICT_TONE[entry.status]}
                            >
                              {key} {entry.status[0]}
                            </StatusPill>
                          ) : null;
                        })}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Toast message={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}

// --- pieces -----------------------------------------------------------------

function RatioBar({ label, value }: { label: string; value: number }) {
  const width = Math.min(1, value / DTI_DOMAIN) * 100;
  return (
    <div className="dti-ratio">
      <div className="dti-ratio__top">
        <span className="dti-ratio__label">{label}</span>
        <span className="dti-ratio__val">{(value * 100).toFixed(1)}%</span>
      </div>
      <div className="dti-ratio__bar">
        <div className="dti-ratio__fill" style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

function VerdictCard({
  programKey,
  entry,
  frontBps,
  backBps,
}: {
  programKey: ProgramKey;
  entry: NonNullable<DtiCalculation["qualification"][ProgramKey]>;
  frontBps: number | null;
  backBps: number | null;
}) {
  const accent = VERDICT_ACCENT[entry.status];
  return (
    <div
      className="dti-vcard"
      style={{ "--dti-accent": accent } as CSSProperties}
    >
      <div className="dti-vcard__head">
        <span className="dti-vcard__prog">{PROGRAM_LABEL[programKey]}</span>
        <span className="dti-verdict">{entry.status}</span>
      </div>
      <DtiMetric
        label="Front DTI"
        valueBps={frontBps}
        limitBps={entry.front_max_bps}
      />
      <DtiMetric
        label="Back DTI"
        valueBps={backBps}
        limitBps={entry.back_max_bps}
      />
      {entry.verified_status !== entry.status && (
        <div className="dti-vcard__note">
          Verified-only: {entry.verified_status}
        </div>
      )}
      {entry.reasons.length > 0 && (
        <div className="dti-vcard__note">{entry.reasons.join(" · ")}</div>
      )}
    </div>
  );
}

function DtiMetric({
  label,
  valueBps,
  limitBps,
}: {
  label: string;
  valueBps: number | null;
  limitBps: number;
}) {
  const value = (valueBps ?? 0) / 10_000;
  const limit = limitBps / 10_000;
  const fill = Math.min(1, value / DTI_DOMAIN) * 100;
  const mark = Math.min(1, limit / DTI_DOMAIN) * 100;
  return (
    <div className="dti-metric">
      <div className="dti-metric__top">
        <span className="dti-metric__label">{label}</span>
        <span className="dti-metric__val">{bpsPct(valueBps)}</span>
      </div>
      <div className="dti-metric__track">
        <div className="dti-metric__fill" style={{ width: `${fill}%` }} />
        <div
          className="dti-metric__mark"
          style={{ left: `${mark}%` }}
          title={`Limit ${bpsPct(limitBps)}`}
        />
      </div>
    </div>
  );
}

function SaveScenario({
  busy,
  onSave,
}: {
  busy: boolean;
  onSave: (name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  if (!open) {
    return (
      <Button
        variant="secondary"
        size="sm"
        disabled={busy}
        onClick={() => setOpen(true)}
      >
        Save scenario…
      </Button>
    );
  }
  return (
    <span className="dti-inline-form">
      <input
        className="dti-select"
        placeholder="Scenario name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <Button
        size="sm"
        disabled={
          busy ||
          name.trim().length === 0 ||
          name.trim().toLowerCase() === "current"
        }
        onClick={() => {
          onSave(name.trim());
          setOpen(false);
          setName("");
        }}
      >
        Save
      </Button>
      <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
        Cancel
      </Button>
    </span>
  );
}

interface OverrideDraft {
  scenario_name?: string;
  kind?: "income" | "debt" | "property_cost";
  category?: string;
  label?: string;
  field_name: string;
  new_value?: unknown;
  reason: string;
}

function LineItemsPanel({
  income,
  debts,
  calc,
  busy,
  onOverride,
  onRemoveOverride,
}: {
  income: readonly DtiLineItem[];
  debts: readonly DtiLineItem[];
  calc: DtiCalculation;
  busy: boolean;
  onOverride: (body: OverrideDraft, label: string) => void;
  onRemoveOverride: (overrideId: string) => void;
}) {
  const [target, setTarget] = useState<DtiLineItem | "loan" | null>(null);
  const [fieldName, setFieldName] = useState("monthly_amount");
  const [newValue, setNewValue] = useState("");
  const [reason, setReason] = useState("");

  function startLine(line: DtiLineItem) {
    setTarget(line);
    setFieldName("monthly_amount");
    setNewValue(String(line.monthly_amount));
    setReason("");
  }

  function submit() {
    if (!target || reason.trim().length < 3) return;
    if (target === "loan") {
      onOverride(
        {
          field_name: fieldName,
          new_value: fieldName === "loan_program" ? newValue : Number(newValue),
          reason: reason.trim(),
        },
        fieldName,
      );
    } else {
      onOverride(
        {
          kind: target.kind,
          category: target.category,
          label: target.label,
          field_name: fieldName,
          new_value:
            fieldName === "is_included"
              ? newValue === "true"
              : Number(newValue),
          reason: reason.trim(),
        },
        target.label,
      );
    }
    setTarget(null);
  }

  return (
    <>
      <div className="dti-section" style={{ marginTop: 24 }}>
        Worksheet lines
        <Button
          variant="ghost"
          size="sm"
          disabled={busy}
          onClick={() => {
            setTarget("loan");
            setFieldName("interest_rate_bps");
            setNewValue("");
            setReason("");
          }}
        >
          Override loan field…
        </Button>
      </div>

      <div className="dti-table-wrap">
        <table className="dti-table">
          <thead>
            <tr>
              <th>Line</th>
              <th>Category</th>
              <th>Source</th>
              <th style={{ textAlign: "right" }}>Monthly</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {[...income, ...debts].map((line, index) => (
              <tr
                key={line.id ?? index}
                className={line.is_included ? "" : "dti-line--off"}
              >
                <td>
                  {line.kind === "income" ? "Income" : "Debt"} · {line.label}
                </td>
                <td>{line.category}</td>
                <td>
                  <code>{line.source}</code>
                </td>
                <td style={{ textAlign: "right" }}>
                  {money(line.monthly_amount)}
                </td>
                <td>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() => startLine(line)}
                  >
                    Override
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {target !== null && (
        <div className="dti-card dti-override-form">
          <div className="dti-card__title">
            {target === "loan"
              ? "Loan-level override"
              : `Override "${target.label}"`}
          </div>
          <div className="dti-inline-form" style={{ marginTop: 10 }}>
            {target === "loan" ? (
              <select
                className="dti-select"
                value={fieldName}
                onChange={(e) => setFieldName(e.target.value)}
              >
                {LOAN_FIELDS.map((field) => (
                  <option key={field} value={field}>
                    {field}
                  </option>
                ))}
              </select>
            ) : (
              <select
                className="dti-select"
                value={fieldName}
                onChange={(e) => setFieldName(e.target.value)}
              >
                <option value="monthly_amount">monthly_amount</option>
                <option value="is_included">is_included</option>
              </select>
            )}
            {fieldName === "is_included" ? (
              <select
                className="dti-select"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
              >
                <option value="false">exclude</option>
                <option value="true">include</option>
              </select>
            ) : (
              <input
                className="dti-select"
                placeholder="New value"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
              />
            )}
          </div>
          <input
            className="dti-select dti-reason"
            placeholder="Reason (required — lenders audit every override)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <div className="dti-btns">
            <Button
              size="sm"
              disabled={busy || reason.trim().length < 3}
              onClick={submit}
            >
              Apply override
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setTarget(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {calc.active_overrides.length > 0 && (
        <>
          <div className="dti-section" style={{ marginTop: 16 }}>
            Active overrides
          </div>
          <div className="dti-table-wrap">
            <table className="dti-table">
              <tbody>
                {calc.active_overrides.map((override) => (
                  <tr key={override.id}>
                    <td>
                      {override.label ?? override.field_name} →{" "}
                      <b>{String(override.new_value)}</b>
                    </td>
                    <td style={{ color: "var(--rp-color-text-muted)" }}>
                      {override.reason}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        onClick={() => onRemoveOverride(override.id)}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
